import { ARRANGEMENT_FEE } from '../../constants/common';
import { formatMoney } from '../../utils/formatMoney';
import { formatNumber } from '../../utils/formatNumber';
import { getEffectiveInterestRate } from '../../utils/getEffectiveInterestRate';
import { getTotalBorrowingCost } from '../../utils/getTotalBorrowingCost';
import {
  getAmortizationAmount,
  getInterestPayment,
} from '../MonthlyLoanCostCard/utils';
import { LOAN_PERIOD_MONTHS, LOAN_PERIOD_YEARS } from './constants';
import { CalculationDetailRow, CalculationDetailsInput } from './types';

const KEY_PREFIX = 'shared:moreInformation.items.calculationDetails';

function formatPercent(value: number): string {
  return `${formatNumber(value, { fixedDecimal: true })}\u00a0%`;
}

/**
 * Pure view-model builder that turns the raw calculation inputs into the list of
 * label/value rows rendered in CalculationDetails
 */
export function buildCalculationDetailsRows({
  loanAmount,
  ltv,
  exampleInterestRate,
  interestRate,
  secondaryApplicantExist,
}: CalculationDetailsInput): CalculationDetailRow[] {
  const monthlyAmortization = getAmortizationAmount(ltv, loanAmount);
  const interestBeforeDeduction = getInterestPayment(
    interestRate,
    loanAmount,
    true,
    secondaryApplicantExist,
  );
  const interestAfterDeduction = getInterestPayment(
    interestRate,
    loanAmount,
    false,
    secondaryApplicantExist,
  );
  const monthlyPaymentBeforeDeduction =
    monthlyAmortization + interestBeforeDeduction;
  const monthlyPaymentAfterDeduction =
    monthlyAmortization + interestAfterDeduction;

  const effectiveInterestRate = getEffectiveInterestRate({
    loanAmount,
    arrangementFee: ARRANGEMENT_FEE,
    annualInterestRate: interestRate,
    numberOfMonths: LOAN_PERIOD_MONTHS,
  });

  const totalBorrowingCost = getTotalBorrowingCost({
    loanAmount,
    annualInterestRate: interestRate,
    monthlyAmortization,
    numberOfMonths: LOAN_PERIOD_MONTHS,
    fees: ARRANGEMENT_FEE,
  });

  return [
    {
      key: 'loan-amount',
      labelKey: `${KEY_PREFIX}.loanAmount`,
      value: formatMoney(loanAmount),
      testId: 'calculation-details-loan-amount',
    },
    {
      key: 'ltv',
      labelKey: `${KEY_PREFIX}.ltv`,
      value: `${formatNumber(Math.trunc(ltv))}\u00a0%`,
      testId: 'calculation-details-ltv',
    },
    {
      key: 'example-interest-rate',
      labelKey: `${KEY_PREFIX}.exampleInterestRate`,
      value: formatPercent(exampleInterestRate),
      testId: 'calculation-details-example-interest-rate',
    },
    {
      key: 'effective-interest-rate',
      labelKey: `${KEY_PREFIX}.effectiveInterestRate`,
      value: formatPercent(effectiveInterestRate),
      tooltipKey: `${KEY_PREFIX}.effectiveInterestRateTooltip`,
      testId: 'calculation-details-effective-interest-rate',
    },
    {
      key: 'loan-period',
      labelKey: `${KEY_PREFIX}.loanPeriod`,
      valueKey: `${KEY_PREFIX}.loanPeriodValue`,
      valueParams: { years: LOAN_PERIOD_YEARS },
      testId: 'calculation-details-loan-period',
    },
    {
      key: 'monthly-amortization',
      labelKey: `${KEY_PREFIX}.monthlyAmortization`,
      value: formatMoney(monthlyAmortization),
      testId: 'calculation-details-monthly-amortization',
    },
    {
      key: 'total-monthly-payment-before-deduction',
      labelKey: `${KEY_PREFIX}.totalMonthlyPaymentBeforeDeduction`,
      value: formatMoney(Math.round(monthlyPaymentBeforeDeduction)),
      testId: 'calculation-details-total-monthly-payment-before-deduction',
    },
    {
      key: 'total-monthly-payment-after-deduction',
      labelKey: `${KEY_PREFIX}.totalMonthlyPaymentAfterDeduction`,
      value: formatMoney(Math.round(monthlyPaymentAfterDeduction)),
      testId: 'calculation-details-total-monthly-payment-after-deduction',
    },
    {
      key: 'total-borrowing-cost',
      labelKey: `${KEY_PREFIX}.totalBorrowingCost`,
      value: formatMoney(Math.round(totalBorrowingCost)),
      isBold: true,
      testId: 'calculation-details-total-borrowing-cost',
    },
  ];
}
